/**
 * Per-contact "Test upload" button for direct-upload (google_ads) locations.
 *
 * Immediately fires a qualified or converted conversion upload via the
 * qualifier's test endpoint and toasts the resulting ledger status — no waiting
 * for the cron. Shown only when the location is in google_ads mode AND the
 * per-location `test_mode_enabled` toggle is on (set via the Google Ads
 * settings dialog). The server endpoint enforces the same per-location toggle.
 */

import { Loader2, Zap } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/use-toast'
import {
  useTestUploadConversion,
  type QualifierConversionEventType,
} from '@/hooks/useGHLQualifierDashboard'

interface Props {
  readonly contactId: string
  readonly locationId: string
  readonly eventType: QualifierConversionEventType
  /** Converted events can carry a value; passed through to the upload. */
  readonly conversionValue?: number | null
}

export function TestUploadButton({ contactId, locationId, eventType, conversionValue }: Props) {
  const { toast } = useToast()
  const mutation = useTestUploadConversion()

  const fire = () => {
    mutation.mutate(
      { contactId, locationId, eventType, conversionValue },
      {
        onSuccess: (res) => {
          if (!res.ok) {
            toast({
              title: 'Upload skipped',
              description: res.message ?? res.reason ?? 'Location is not in Google Ads mode.',
            })
            return
          }
          toast({
            title: `${eventType} upload: ${res.status}${res.validateOnly ? ' (validateOnly)' : ''}`,
            description: res.error ? `Error: ${res.error}` : `transaction ${res.transactionId}`,
            variant: res.status === 'failed' ? 'destructive' : 'default',
          })
        },
        onError: (err: unknown) => {
          toast({
            title: 'Test upload failed',
            description: err instanceof Error ? err.message : 'Unknown error',
            variant: 'destructive',
          })
        },
      }
    )
  }

  return (
    <Button
      size="sm"
      variant="outline"
      className="h-7 gap-1.5"
      disabled={mutation.isPending}
      onClick={(e) => {
        e.stopPropagation() // don't trigger row navigation
        fire()
      }}
    >
      {mutation.isPending ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <Zap className="h-3.5 w-3.5" />
      )}
      Test upload
    </Button>
  )
}
